import {SERIES_COLORS} from '../../consts'
import {ValueDisplayMode, YearlyHierarchyAnalysisDataPoint} from '../../types'

import {DifficultyTotals} from './chartTooltip'

interface HierarchyEntry {
  id: string
  name: string
}

const computeValue = (answeredPositively: number, questionCount: number, mode: ValueDisplayMode) =>
  mode === 'percentage' ? Math.round((answeredPositively / questionCount) * 100) : answeredPositively

export const buildYearlySeriesData = (
  hierarchyEntries: HierarchyEntry[],
  yearlyData: YearlyHierarchyAnalysisDataPoint[],
  years: number[],
  mode: ValueDisplayMode = 'total'
) =>
  hierarchyEntries.map((entry, index) => {
    const nodeData = yearlyData.filter((dp) => dp.hierarchyId === entry.id)

    const seriesData = years.map((year) => {
      const point = nodeData.find((dp) => dp.year === year)
      if (!point) return null

      const scores = point.difficultyScores
      if (scores) {
        const answered =
          (scores.basic?.answeredPositively ?? 0) +
          (scores.advanced?.answeredPositively ?? 0) +
          (scores.excellence?.answeredPositively ?? 0)
        const total =
          (scores.basic?.questionCount ?? 0) +
          (scores.advanced?.questionCount ?? 0) +
          (scores.excellence?.questionCount ?? 0)
        return total > 0 ? computeValue(answered, total, mode) : answered
      }
      const totalScore = point.totalScore
      if (totalScore && totalScore.questionCount > 0) {
        return computeValue(totalScore.answeredPositively, totalScore.questionCount, mode)
      }
      return totalScore?.answeredPositively ?? null
    })

    const color = SERIES_COLORS[index % SERIES_COLORS.length]

    return {
      name: entry.name,
      type: 'line' as const,
      data: seriesData,
      smooth: true,
      emphasis: {focus: 'none' as const},
      zlevel: 1,
      lineStyle: {color},
      itemStyle: {color}
    }
  })

export const buildPerNodeYearlyDifficulty = (
  hierarchyEntries: HierarchyEntry[],
  yearlyData: YearlyHierarchyAnalysisDataPoint[],
  years: number[]
): Map<string, Array<DifficultyTotals | null>> => {
  const result = new Map<string, Array<DifficultyTotals | null>>()

  for (const entry of hierarchyEntries) {
    const nodeData = years.map((year) => {
      const point = yearlyData.find((dp) => dp.hierarchyId === entry.id && dp.year === year)

      if (point?.difficultyScores) {
        return {
          basic: {
            answered: point.difficultyScores.basic?.answeredPositively ?? 0,
            total: point.difficultyScores.basic?.questionCount ?? 0
          },
          advanced: {
            answered: point.difficultyScores.advanced?.answeredPositively ?? 0,
            total: point.difficultyScores.advanced?.questionCount ?? 0
          },
          excellence: {
            answered: point.difficultyScores.excellence?.answeredPositively ?? 0,
            total: point.difficultyScores.excellence?.questionCount ?? 0
          }
        }
      }

      return null
    })
    result.set(entry.name, nodeData)
  }

  return result
}
